import {
  type ExamEventSession,
  type ExamCourseSettings,
  type ExamEventCourseStatus,
} from '@exam-center/graphql';
import { type OptionSelectionProps, Table, TableContainer } from '@shared/components';
import { TableHeader, TableBody } from '@shared/table-generator';
import { useMemo } from 'react';

import { getColumns } from './columns';
import { createRows } from './utils';

export interface EditQSetIdNumberFormProps {
  data: {
    examEvent: {
      examSessions: Pick<ExamEventSession, 'id' | 'startedAt' | 'endedAt' | 'round'>[];
    };
    examEventCourses: {
      id: string;
      examCourse: {
        code: string;
        status: ExamEventCourseStatus;
        examId: number;
        settings: Partial<Pick<ExamCourseSettings, 'examDuration'>>;
      };
      options: OptionSelectionProps[];
      value: OptionSelectionProps['value'];
    }[];
  };
}

export default function EditQSetIdNumberForm(props: Readonly<EditQSetIdNumberFormProps>) {
  const { data } = props;
  const { examEvent, examEventCourses } = data;

  const columns = useMemo(() => {
    return getColumns(examEventCourses);
  }, [examEventCourses]);

  const rows = useMemo(() => {
    return createRows(examEvent.examSessions, examEventCourses);
  }, [examEvent, examEventCourses]);

  return (
    <TableContainer
      sx={(theme) => ({
        border: `1px solid ${theme.palette.grey[300]}`,
        borderRadius: theme.shape.rounded(2),
      })}
    >
      <Table>
        <TableHeader
          columns={columns}
          slotProps={{
            tableHeaderRow: {
              sx: (theme) => ({
                '& > th': {
                  borderRight: `1px solid ${theme.palette.grey[300]}`,
                  borderBottom: `1px solid ${theme.palette.grey[300]}`,
                },
                ':first-of-type': {
                  'th:last-of-type': {
                    borderRight: 'none',
                  },
                },
              }),
            },
            tableHeaderCell: {
              sx: (theme) => ({
                ...theme.typography.body1Semibold,
                color: theme.palette.common.white,
                backgroundColor: theme.palette.grey[800],
                p: theme.spacing(2),
              }),
            },
          }}
        />
        <TableBody
          rows={rows}
          slotProps={{
            tableBodyCell: {
              sx: (theme) => ({
                ...theme.typography.body1,
                borderRight: `1px solid ${theme.palette.grey[300]}`,
                p: theme.spacing(2),
              }),
            },
          }}
        />
      </Table>
    </TableContainer>
  );
}
